import { Ref, ref } from 'vue';
import { useLocale } from '../../../composables/useLocale';
import { Role } from '../../../types/user';

type NewUser = {
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  password: string | null;
  role: string | null;
  isAdministrator: boolean;
  hasSpeakingBookRights: boolean;
  organizations: string[] | null;
  addToSubOrganization: boolean;
  identifiers: { type: string; value: string }[] | null;
};

type FormUser = {
  uid: string | null;
  legacyId: string | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  userName: string | null;
  email: string | null;
  emailVerified: boolean | null;
  password: string | null;
  confirmPassword: string | null;
  active: boolean | null;
  ageFilter: number | null;
  shared: boolean | null;
  createdAt: string | null;
  updatedAt: string | null;
  deactivatedAt: string | null;
  guest: boolean | null;
  hasSpeakingBookRights: boolean | null;
  forcePasswordChange: boolean | null;
  hidePasswordChange: boolean | null;
  externalId: string | null;
  identifiers: { type: string; value: string }[];
  role: Role | null;
};
type CustomerSubscriptionField = {
  type: string;
  startDate: string;
  endDate: string;
};

type ConflictErrors = {
  attribute: string;
  reason: string;
};

type ValidationErrors = {
  attribute: string;
  reason: string;
};

const errors: Ref<ConflictErrors[] | ValidationErrors[]> = ref([]);
function useUserValidation<T>(newUser = false) {
  const { t } = useLocale();

  const initUser: FormUser = {
    uid: null, // just to satisfy the type
    legacyId: null, // just to satisfy the type

    name: null,
    firstName: null,
    lastName: null,
    userName: null,
    email: null,
    emailVerified: null, // just to satisfy the type

    role: null,

    password: null,
    confirmPassword: null,

    active: null,
    ageFilter: null,
    shared: null,

    createdAt: null,
    updatedAt: null,
    deactivatedAt: null,

    guest: null,
    hasSpeakingBookRights: null,
    forcePasswordChange: null,
    hidePasswordChange: null,

    externalId: null,

    identifiers: [],
  };

  const initNewUser: NewUser = {
    userName: null,
    firstName: null,
    lastName: null,
    email: null,
    password: null,
    role: null,
    isAdministrator: false,
    hasSpeakingBookRights: false,
    organizations: null,
    addToSubOrganization: false,
    identifiers: null,
  };

  const formUser = ref<FormUser | NewUser>(newUser ? initNewUser : initUser);
  const usedUserNames: Ref<string[]> = ref([]);
  const findErrorReason = (attribute: string) => {
    if (errors.value.some((error) => error.attribute === attribute)) {
      const reason = errors.value.find((error) => error.attribute === attribute)?.reason;
      errors.value = errors.value.filter((error) => error.attribute !== attribute);
      return reason;
    }

    return false;
  };

  const rules = {
    required: (v: string | null) => !!v || t('lcp.required'),
    notEmpty: (v: string | null) => (v !== null && v.length > 0) || t('lcp.required'),
    safeText: (v: string | null) => {
      if (v !== null && !/^[A-Za-z0-9._\-~()'!*:@,;]*$/.test(v)) return t('lcp.value_invalid');

      return true;
    },
    htmlSafe: (v: string | null) => {
      // To match with user API validatation
      if (v !== null && /[<>=]/.test(v)) return t('lcp.value_invalid');
      return true;
    },
    userName: (v: string | null | undefined) => {
      const errorMsg = findErrorReason('userName');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      const pattern = /^[A-Za-z0-9\-@_%#*.,!\x20]+$/;
      if (!pattern.test(v)) return t('lcp.value_invalid');
      if (v.length > 255) return t('lcp.at_most_max_length_characters', { max_length: 255 });
      if (v.length < 1) return t('lcp.at_least_min_length_characters', { min_length: 1 });
      if (usedUserNames.value.includes(v)) return t('lcp.username_taken');
      return true;
    },
    name: (v: string | null) => {
      const errorMsg = findErrorReason('name');
      if (errorMsg) return errorMsg;
      if (v === null) return true;
      if (v !== null && v.length > 255) return t('lcp.at_most_max_length_characters', { max_length: 255 });
      if (v !== null && v.length < 1) return t('lcp.at_least_min_length_characters', { min_length: 1 });
      return true;
    },
    firstName: (v: string | null) => {
      const errorMsg = findErrorReason('firstName');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      if (v !== null && v.length > 255) return t('lcp.at_most_max_length_characters', { max_length: 255 });
      return true;
    },
    lastName: (v: string | null) => {
      const errorMsg = findErrorReason('lastName');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      if (v !== null && v.length > 255) return t('lcp.at_most_max_length_characters', { max_length: 255 });
      return true;
    },
    email: (v: string | null) => {
      const errorMsg = findErrorReason('email');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      // matches ajv-formats "email" to make sure frontend and backend validation are aligned
      // see https://github.com/ajv-validator/ajv-formats/blob/master/src/formats.ts
      const pattern =
        /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
      if (!pattern.test(v)) return t('lcp.email_invalid');
      if (v.length > 255) return t('lcp.at_most_max_length_characters', { max_length: 255 });
      return true;
    },
    role: (v: string | null) => {
      const errorMsg = findErrorReason('role');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      if (['student', 'employee', 'guardian', 'admin'].includes(v) === false) {
        return t('lcp.role_invalid');
      }

      return ['student', 'employee', 'guardian', 'admin'].includes(v.toLowerCase().trim()) === true;
    },
    password: (v: string | null | undefined) => {
      const errorMsg = findErrorReason('password');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      if (v.length < 8) return t('lcp.at_least_min_length_characters', { min_length: 8 });
      return true;
    },
    confirmPassword: (v: string | null) => {
      const errorMsg = findErrorReason('userName');
      if (errorMsg) return errorMsg;
      if (!v) return true;
      if (v.length < 8) return t('lcp.at_least_min_length_characters', { min_length: 8 });

      return (
        (rules.password(formUser.value.password) === true && v === formUser.value.password) ||
        t('lcp.must_match_password')
      );
    },

    eppn: (v: string) => {
      if (!v) return true;
      const errorMsg = findErrorReason('eppn');
      if (errorMsg) return errorMsg;
      return true;
    },
    office365id: (v: string) => {
      if (!v) return true;
      const errorMsg = findErrorReason('office365id');
      if (errorMsg) return errorMsg;
      return true;
    },
    gsuiteemail: (v: string) => {
      if (!v) return true;
      const errorMsg = findErrorReason('gsuiteemail');
      if (errorMsg) return errorMsg;
      return true;
    },

    sms: (v: string) => {
      if (!v) return true;
      const pattern = /^\+[1-9]\d{6,14}$/;
      if (!pattern.test(v)) return t('lcp.sms_invalid');
      const errorMsg = findErrorReason('sms');
      if (errorMsg) return errorMsg;
      return true;
    },
    bankid: (v: string) => {
      if (!v) return true;
      const pattern = /^((19)|(2[0-3]))\d{2}((0[1-9])|(1[0-2]))((0[1-9])|(1[0-9])|(2[0-9])|(3[0-1]))\d{4}$/;
      if (!pattern.test(v)) return t('lcp.bankid_invalid');
      const errorMsg = findErrorReason('bankid');
      if (errorMsg) return errorMsg;
      return true;
    },
    subscription: (v: CustomerSubscriptionField) => {
      if (!v || v === null) return true;

      if (rules.notEmpty(v.type) === true) {
        if (rules.notEmpty(v.startDate) === true && rules.notEmpty(v.endDate) === true) {
          return true;
        }

        return { startDate: rules.notEmpty(v.startDate), endDate: rules.notEmpty(v.endDate) };
      }

      const errorMsg = findErrorReason('subscription');
      if (errorMsg) return errorMsg;
      return true;
    },
  };

  const getIdentifierRules = (type: string) => {
    switch (type) {
      case 'eppn':
        return [rules.eppn, rules.email];
      case 'office365id':
        return [rules.office365id, rules.email];
      case 'gsuiteemail':
        return [rules.gsuiteemail, rules.email];
      case 'sms':
        return [rules.sms];
      case 'bankid':
        return [rules.bankid];
      default:
        return [rules.safeText];
    }
  };

  return {
    formUser: formUser as Ref<T>,
    rules,
    errors,
    usedUserNames,
    getIdentifierRules,
  };
}

export default useUserValidation;
